<template>
  <div v-if="open" class="test-runner">
    <div class="test-topbar">
      <div class="logo" style="margin-right:8px">
        <div class="logo-mark"></div>
        <span style="color:#fff;font-weight:800;font-size:16px">measure<span style="color:var(--accent)">Up</span></span>
      </div>
      <div class="test-exam-title">{{ examTitle }}</div>
      <div class="test-topbar-meta">
        <span class="test-save-indicator">Saved · v{{ saveVersion }}</span>
        <span class="test-timer">
          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg>
          {{ timerDisplay }}
        </span>
        <button class="test-close" @click="exitTest">Exit test</button>
      </div>
    </div>

    <div class="test-body">
      <main class="test-main">
        <div class="test-container" v-if="currentQ">
          <div class="test-q-head">
            <div class="test-q-num">Question {{ currentIndex + 1 }} of {{ total }}</div>
            <div class="test-q-chips">
              <span class="pill neutral">{{ typeLabel }}</span>
              <span v-for="tag in currentQ.tags" :key="tag" class="pill neutral">{{ tag }}</span>
            </div>
          </div>

          <div class="test-q-prompt" v-html="currentQ.prompt"></div>
          <div v-if="currentQ.context" class="test-q-context" v-html="currentQ.context"></div>

          <div class="test-answers">
            <div
              v-for="(answer, i) in currentQ.answers"
              :key="i"
              class="test-answer"
              :class="answerClass(i)"
              @click="toggleAnswer(i)"
            >
              <div class="test-answer-letter">
                <template v-if="isMulti">
                  <svg v-if="isSelected(i)" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round"><polyline points="20 6 9 17 4 12"/></svg>
                  <span v-else>{{ letters[i] }}</span>
                </template>
                <template v-else>{{ letters[i] }}</template>
              </div>
              <div class="test-answer-text" v-html="answer"></div>
              <span class="test-answer-kbd">{{ letters[i] }}</span>
            </div>
          </div>

          <!-- Feedback panel: appears after Check answer -->
          <transition name="fade-in">
            <div v-if="checked" class="test-feedback" :class="{ correct: isCorrect, wrong: !isCorrect }">
              <div class="test-feedback-head">
                <svg v-if="isCorrect" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round"><polyline points="20 6 9 17 4 12"/></svg>
                <svg v-else width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
                <span class="test-feedback-title">{{ isCorrect ? 'Correct' : 'Incorrect' }}</span>
                <span v-if="!isCorrect" class="test-feedback-correct-answer">
                  Correct answer: <b>{{ correctLettersDisplay }}</b>
                </span>
              </div>

              <div v-if="!isCorrect" class="test-feedback-body">
                <div v-html="currentQ.explanation"></div>

                <div class="test-ai-row">
                  <button class="btn-ai" :disabled="!isUnlocked && tokensLeft === 0" @click="onAiClick">
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round"><polygon points="12 2 15 8.5 22 9.3 17 14.1 18.2 21 12 17.8 5.8 21 7 14.1 2 9.3 9 8.5 12 2"/></svg>
                    <template v-if="isUnlocked">{{ aiOpen ? 'Hide IA Explanation' : 'Show IA Explanation' }}</template>
                    <template v-else>IA Explanation</template>
                    <span v-if="!isUnlocked" class="btn-ai-cost">
                      <svg width="11" height="11" viewBox="0 0 24 24" fill="currentColor"><circle cx="12" cy="12" r="10"/></svg>
                      1 token
                    </span>
                    <svg v-if="isUnlocked" width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" :style="{ transform: aiOpen ? 'rotate(180deg)' : '' }"><polyline points="6 9 12 15 18 9"/></svg>
                  </button>

                  <span v-if="!isUnlocked" class="test-ai-balance" :class="{ low: tokensLeft === 0 }">
                    <svg width="11" height="11" viewBox="0 0 24 24" fill="currentColor" style="color:var(--warning)"><circle cx="12" cy="12" r="10"/></svg>
                    {{ tokensLeft }} / {{ tokensTotal }} tokens left
                  </span>
                </div>

                <transition name="fade-in">
                  <div v-if="!isUnlocked && tokensLeft === 0" class="test-ai-paywall">
                    <div class="test-ai-paywall-title">
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round"><rect x="3" y="11" width="18" height="11" rx="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/></svg>
                      You've used all your IA Explanation tokens
                    </div>
                    <div class="test-ai-paywall-body">
                      Each detailed AI-generated explanation costs 1 token. Top up your balance to keep using this feature.
                    </div>
                    <button class="btn-ai" style="margin-top:10px" @click="openTokenPurchase">
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
                      Buy tokens
                    </button>
                  </div>
                </transition>

                <transition name="fade-in">
                  <div v-if="isUnlocked && aiOpen" class="test-ai-panel">
                    <div class="test-ai-head">
                      <span class="test-ai-badge">AI</span>
                      <span>Detailed explanation</span>
                    </div>
                    <div v-if="aiLoading" class="test-ai-body">Thinking…</div>
                    <div v-else class="test-ai-body" style="white-space:pre-wrap">{{ aiAnswer }}</div>
                  </div>
                </transition>
              </div>
            </div>
          </transition>

          <div class="test-actions">
            <div style="display:flex;gap:8px">
              <button class="btn-ghost" :disabled="currentIndex === 0" @click="goPrev">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round"><polyline points="15 18 9 12 15 6"/></svg>
                Previous
              </button>
              <button class="btn-ghost" :class="{ flagged: isFlagged(currentIndex) }" @click="toggleFlag(currentIndex)">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"><path d="M4 15s1-1 4-1 5 2 8 2 4-1 4-1V3s-1 1-4 1-5-2-8-2-4 1-4 1z"/><line x1="4" y1="22" x2="4" y2="15"/></svg>
                {{ isFlagged(currentIndex) ? 'Flagged' : 'Flag for review' }}
              </button>
            </div>

            <button v-if="!checked" class="btn-primary" @click="checkAnswer">
              Check answer
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round"><polyline points="20 6 9 17 4 12"/></svg>
            </button>
            <button v-else-if="currentIndex < total - 1" class="btn-primary" @click="goNext">
              Next question
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round"><polyline points="9 18 15 12 9 6"/></svg>
            </button>
            <button v-else class="btn-primary" @click="submitExam">
              Submit exam
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round"><polyline points="20 6 9 17 4 12"/></svg>
            </button>
          </div>
        </div>
      </main>

      <aside class="test-nav-panel">
        <div class="test-nav-title">Question Navigator</div>
        <div class="test-nav-grid">
          <div
            v-for="n in total"
            :key="n"
            class="test-nav-cell"
            :class="navCellClass(n - 1)"
            @click="jumpTo(n - 1)"
          >{{ n }}</div>
        </div>

        <div class="test-nav-legend">
          <div class="test-nav-legend-item"><span class="swatch correct"></span>Correct</div>
          <div class="test-nav-legend-item"><span class="swatch wrong"></span>Incorrect</div>
          <div class="test-nav-legend-item"><span class="swatch" style="background:var(--accent)"></span>Current</div>
          <div class="test-nav-legend-item"><span class="swatch answered"></span>Answered</div>
          <div class="test-nav-legend-item"><span class="swatch" style="background:#fff;border:1px solid var(--border)"></span>Not answered</div>
          <div class="test-nav-legend-item"><span class="swatch" style="background:var(--warning)"></span>Flagged</div>
        </div>

        <div class="test-summary">
          <div class="test-summary-row"><span class="label">Answered</span><span class="value">{{ answeredCount }} / {{ total }}</span></div>
          <div class="test-summary-row"><span class="label">Correct so far</span><span class="value">{{ correctCount }}</span></div>
          <div class="test-summary-row"><span class="label">Flagged</span><span class="value">{{ flagged.size }}</span></div>
          <div class="test-summary-row"><span class="label">Time left</span><span class="value">{{ timerDisplay }}</span></div>
        </div>

        <button class="btn-primary" style="width:100%" @click="submitExam">Submit exam</button>
      </aside>
    </div>
  </div>
</template>

<script>
import { CATALOG } from '../data/catalog.js';
import { getQuestionsForTest } from '../data/questions.js';

export default {
  name: 'TestRunnerOverlay',
  data() {
    return {
      letters: ['A', 'B', 'C', 'D', 'E', 'F'],
      questions: [],
      currentIndex: 0,
      // selections: { [qIndex]: number | number[] }
      selections: {},
      // checked: { [qIndex]: { correct: boolean } }
      checkedMap: {},
      flagged: new Set(),
      aiOpen: false,
      // Set of question indices for which an IA Explanation token has
      // already been spent — once unlocked the user can re-toggle freely.
      aiUnlocked: new Set(),
      // A11: respuestas reales del Tutor IA por índice de pregunta (antes era un
      // string estático `currentQ.aiExplanation`). Ahora vienen del modelo.
      aiAnswers: {},
      aiLoading: false,
      timerSeconds: 90 * 60,
      timerInterval: null,
      saveVersion: 1,
      saveBumpInterval: null,
      examTitle: '',
    };
  },
  computed: {
    open() { return this.$store.state.ui.testRunner.open; },
    courseId() { return this.$store.state.ui.testRunner.courseId; },
    total() { return this.questions.length; },
    currentQ() { return this.questions[this.currentIndex] || null; },
    isMulti() { return this.currentQ && this.currentQ.type === 'multi'; },
    typeLabel() { return this.isMulti ? 'Multiple choice' : 'Single choice'; },
    currentSelection() {
      const v = this.selections[this.currentIndex];
      if (this.isMulti) return Array.isArray(v) ? v : [];
      return typeof v === 'number' ? v : null;
    },
    checked() { return !!this.checkedMap[this.currentIndex]; },
    isCorrect() { return this.checked && this.checkedMap[this.currentIndex].correct; },
    correctLettersDisplay() {
      if (!this.currentQ) return '';
      const c = this.currentQ.correct;
      const indices = Array.isArray(c) ? c : [c];
      return indices.map(i => this.letters[i]).join(', ');
    },
    answeredCount() {
      return Object.keys(this.selections).filter(k => {
        const v = this.selections[k];
        return Array.isArray(v) ? v.length > 0 : typeof v === 'number';
      }).length;
    },
    correctCount() {
      return Object.values(this.checkedMap).filter(r => r && r.correct).length;
    },
    tokensLeft() { return this.$store.state.tokens.left; },
    tokensTotal() { return this.$store.state.tokens.total; },
    isUnlocked() { return this.aiUnlocked.has(this.currentIndex); },
    aiAnswer() { return this.aiAnswers[this.currentIndex] || ''; },
    timerDisplay() {
      const s = Math.max(0, this.timerSeconds);
      const h = String(Math.floor(s / 3600)).padStart(2, '0');
      const m = String(Math.floor((s % 3600) / 60)).padStart(2, '0');
      const sec = String(s % 60).padStart(2, '0');
      return `${h}:${m}:${sec}`;
    },
  },
  watch: {
    open(val) {
      if (val) this.startTest();
      else this.tearDown();
    },
    currentIndex() {
      // Reset AI panel when navigating
      this.aiOpen = false;
    },
  },
  mounted() {
    if (this.open) this.startTest();
  },
  beforeDestroy() { this.tearDown(); },
  methods: {
    startTest() {
      const test = CATALOG.find(c => c.id === this.courseId) || CATALOG[0];
      this.examTitle = test ? test.title : 'Practice test';
      this.questions = getQuestionsForTest(test);
      this.currentIndex = 0;
      this.selections = {};
      this.checkedMap = {};
      this.flagged = new Set();
      this.aiOpen = false;
      this.aiUnlocked = new Set();
      this.aiAnswers = {};
      this.aiLoading = false;
      this.timerSeconds = 90 * 60;
      this.saveVersion = 1;

      // A11: sincroniza el saldo real de tokens del Tutor IA (server-side).
      this.$store.dispatch('fetchTokens');

      this.timerInterval = setInterval(() => {
        if (this.timerSeconds > 0) this.timerSeconds--;
      }, 1000);
      this.saveBumpInterval = setInterval(() => { this.saveVersion++; }, 12000);
      window.addEventListener('keydown', this.onKey);
    },
    tearDown() {
      clearInterval(this.timerInterval);
      clearInterval(this.saveBumpInterval);
      window.removeEventListener('keydown', this.onKey);
    },
    toggleAnswer(i) {
      if (this.checked) return; // lock after check
      if (this.isMulti) {
        const cur = Array.isArray(this.selections[this.currentIndex])
          ? this.selections[this.currentIndex].slice()
          : [];
        const at = cur.indexOf(i);
        if (at >= 0) cur.splice(at, 1);
        else cur.push(i);
        cur.sort((a, b) => a - b);
        this.$set(this.selections, this.currentIndex, cur);
      } else {
        this.$set(this.selections, this.currentIndex, i);
      }
    },
    isSelected(i) {
      if (this.isMulti) {
        return Array.isArray(this.currentSelection) && this.currentSelection.includes(i);
      }
      return this.currentSelection === i;
    },
    answerClass(i) {
      const selected = this.isSelected(i);
      if (!this.checked) return { selected };
      const correct = this.currentQ.correct;
      const correctSet = Array.isArray(correct) ? correct : [correct];
      const isCorrectOption = correctSet.includes(i);
      return {
        selected,
        'is-correct': isCorrectOption,
        'is-wrong': selected && !isCorrectOption,
      };
    },
    checkAnswer() {
      const sel = this.currentSelection;
      const empty = this.isMulti ? (!Array.isArray(sel) || sel.length === 0) : sel === null;
      if (empty) {
        this.$store.dispatch('toast', 'Select an answer first');
        return;
      }
      const correct = this.currentQ.correct;
      let isCorrect;
      if (this.isMulti) {
        const a = sel.slice().sort();
        const b = (Array.isArray(correct) ? correct : [correct]).slice().sort();
        isCorrect = a.length === b.length && a.every((v, i) => v === b[i]);
      } else {
        isCorrect = sel === correct;
      }
      this.$set(this.checkedMap, this.currentIndex, { correct: isCorrect });
      this.aiOpen = false;
    },
    goNext() {
      if (this.currentIndex < this.total - 1) this.currentIndex++;
    },
    goPrev() {
      if (this.currentIndex > 0) this.currentIndex--;
    },
    jumpTo(i) {
      if (i >= 0 && i < this.total) this.currentIndex = i;
    },
    toggleFlag(i) {
      if (this.flagged.has(i)) this.flagged.delete(i);
      else this.flagged.add(i);
      // Force reactivity for Set
      this.flagged = new Set(this.flagged);
    },
    isFlagged(i) { return this.flagged.has(i); },
    navCellClass(i) {
      const cls = [];
      if (i === this.currentIndex) cls.push('current');
      if (this.checkedMap[i]) {
        cls.push(this.checkedMap[i].correct ? 'correct' : 'wrong');
      } else if (this.selections[i] !== undefined) {
        const v = this.selections[i];
        const has = Array.isArray(v) ? v.length > 0 : typeof v === 'number';
        if (has) cls.push('answered');
      }
      if (this.flagged.has(i)) cls.push('flagged');
      return cls.join(' ');
    },
    onKey(e) {
      if (!this.open || !this.currentQ) return;
      const key = e.key.toUpperCase();
      const idx = this.letters.indexOf(key);
      if (idx >= 0 && idx < this.currentQ.answers.length) {
        e.preventDefault();
        this.toggleAnswer(idx);
        return;
      }
      if (e.key === 'Enter') {
        e.preventDefault();
        if (!this.checked) this.checkAnswer();
        else if (this.currentIndex < this.total - 1) this.goNext();
        else this.submitExam();
      } else if (e.key === 'ArrowRight') {
        if (this.checked) this.goNext();
      } else if (e.key === 'ArrowLeft') {
        this.goPrev();
      }
    },
    onAiClick() {
      // Already unlocked for this question — just toggle visibility, no charge.
      if (this.aiUnlocked.has(this.currentIndex)) {
        this.aiOpen = !this.aiOpen;
        return;
      }
      // Need to spend a token. Out of balance → paywall, no toggle.
      if (this.tokensLeft <= 0) {
        this.$store.dispatch('toast', 'Out of IA Explanation tokens — top up to continue');
        return;
      }
      this.askAiTutor();
    },
    // A11 (PE-10): consulta REAL al Tutor IA. Consume 1 token server-side y
    // muestra la respuesta del modelo (antes revelaba un string estático).
    // Degrada con gracia: 402 NO_TOKENS (sin saldo) y 503 (IA no disponible)
    // no bloquean el runner ni pierden el token.
    async askAiTutor() {
      if (this.aiLoading) return;
      const q = this.currentQ;
      if (!q) return;
      this.aiLoading = true;
      // Marcamos como desbloqueada y abierta para mostrar el "Thinking…".
      this.aiUnlocked = new Set(this.aiUnlocked).add(this.currentIndex);
      this.aiOpen = true;
      try {
        const context = {
          topic: (q.tags && q.tags[0]) || this.examTitle || null,
          prompt: this.stripHtml(q.prompt),
          options: Array.isArray(q.answers) ? q.answers.map(this.stripHtml) : [],
          correct_answer: this.correctLettersDisplay || null,
        };
        const data = await this.$store.dispatch('askTutor', {
          question: 'Explain why the correct answer is right and why my choice was wrong.',
          context,
        });
        this.$set(this.aiAnswers, this.currentIndex, (data && data.answer) || 'No response.');
      } catch (e) {
        const status = e && e.response && e.response.status;
        const code = e && e.response && e.response.data && e.response.data.code;
        // Revertimos el desbloqueo local: no se ha gastado token en el servidor.
        const next = new Set(this.aiUnlocked); next.delete(this.currentIndex);
        this.aiUnlocked = next;
        this.aiOpen = false;
        if (status === 402 || code === 'NO_TOKENS') {
          this.$store.dispatch('fetchTokens');
          this.$store.dispatch('toast', 'Out of IA Explanation tokens — top up to continue');
        } else if (status === 503 || code === 'AI_UNAVAILABLE') {
          this.$store.dispatch('toast', 'The AI tutor is temporarily unavailable');
        } else {
          this.$store.dispatch('toast', 'Could not reach the AI tutor');
        }
      } finally {
        this.aiLoading = false;
      }
    },
    stripHtml(html) {
      if (typeof html !== 'string') return html;
      return html.replace(/<[^>]*>/g, '').trim();
    },
    openTokenPurchase() {
      this.$store.commit('OPEN_TOKEN_PURCHASE');
    },
    exitTest() { this.$store.commit('CLOSE_TEST_RUNNER'); },
    submitExam() {
      this.$store.commit('CLOSE_TEST_RUNNER');
      this.$store.dispatch('toast', `Exam submitted — ${this.correctCount}/${this.total} correct`);
      setTimeout(() => this.$router.push('/results/1').catch(() => {}), 800);
    },
  },
};
</script>

<style scoped>
.test-feedback {
  margin-top: 18px;
  padding: 14px 16px;
  border-radius: 10px;
  border: 1px solid;
  font-size: 13.5px;
  line-height: 1.55;
}
.test-feedback.correct {
  background: #ecfdf5;
  border-color: #a7f3d0;
  color: #065f46;
}
.test-feedback.wrong {
  background: #fef2f2;
  border-color: #fecaca;
  color: #7f1d1d;
}
.test-feedback-head {
  display: flex;
  align-items: center;
  gap: 8px;
  font-weight: 700;
  font-size: 14px;
}
.test-feedback-title { letter-spacing: -0.01em; }
.test-feedback-correct-answer {
  margin-left: auto;
  font-weight: 500;
  font-size: 12.5px;
  opacity: 0.85;
}
.test-feedback-body {
  margin-top: 10px;
  color: var(--ink);
}
.test-feedback.wrong .test-feedback-body { color: #4b1d1d; }
.test-feedback-body :deep(code) {
  background: rgba(0,0,0,0.06);
  padding: 1px 5px;
  border-radius: 4px;
  font-size: 12px;
  font-family: var(--font-mono, monospace);
}

.test-ai-row {
  margin-top: 12px;
  display: flex;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
}
.btn-ai-cost {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  margin-left: 4px;
  padding: 2px 8px;
  background: rgba(255, 255, 255, 0.22);
  border-radius: 99px;
  font-size: 11.5px;
  font-weight: 700;
  letter-spacing: 0.02em;
}
.btn-ai-cost svg { color: #fde68a; }
.btn-ai:disabled {
  opacity: 0.55;
  cursor: not-allowed;
  filter: grayscale(0.4);
}
.btn-ai:disabled:hover { transform: none; box-shadow: 0 2px 8px rgba(99, 102, 241, 0.20); }
.test-ai-balance {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  font-size: 12px;
  color: var(--muted-2);
  font-weight: 500;
}
.test-ai-balance.low { color: var(--danger); }

.test-ai-paywall {
  margin-top: 12px;
  padding: 14px 16px;
  background: linear-gradient(180deg, #fff7ed 0%, #fef3c7 100%);
  border: 1px solid #fed7aa;
  border-radius: 10px;
  color: #78350f;
}
.test-ai-paywall-title {
  display: inline-flex;
  align-items: center;
  gap: 7px;
  font-weight: 700;
  font-size: 13.5px;
  letter-spacing: -0.01em;
}
.test-ai-paywall-body {
  margin-top: 6px;
  font-size: 13px;
  line-height: 1.5;
  color: #78350f;
}
.btn-ai {
  display: inline-flex;
  align-items: center;
  gap: 7px;
  padding: 7px 14px;
  background: linear-gradient(135deg, #6366f1 0%, #8b5cf6 50%, #ec4899 100%);
  color: #fff;
  border: none;
  border-radius: 8px;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  transition: transform 0.13s, box-shadow 0.13s, filter 0.13s;
  box-shadow: 0 2px 8px rgba(99, 102, 241, 0.35);
}
.btn-ai:hover {
  transform: translateY(-1px);
  filter: brightness(1.08);
  box-shadow: 0 4px 14px rgba(99, 102, 241, 0.45);
}
.btn-ai:active { transform: translateY(0); }
.btn-ai svg { transition: transform 0.18s; }

.test-ai-panel {
  margin-top: 12px;
  padding: 14px 16px;
  background: linear-gradient(180deg, #faf5ff 0%, #f5f3ff 100%);
  border: 1px solid #e9d5ff;
  border-radius: 10px;
  color: #1f2937;
  font-size: 13.5px;
  line-height: 1.6;
}
.test-ai-head {
  display: flex;
  align-items: center;
  gap: 8px;
  font-weight: 700;
  color: #6d28d9;
  font-size: 13px;
  margin-bottom: 8px;
  text-transform: uppercase;
  letter-spacing: 0.06em;
}
.test-ai-badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 26px;
  height: 18px;
  background: linear-gradient(135deg, #6366f1, #8b5cf6);
  color: #fff;
  border-radius: 4px;
  font-size: 10px;
  font-weight: 800;
  letter-spacing: 0.04em;
}
.test-ai-body :deep(code) {
  background: rgba(99, 102, 241, 0.10);
  color: #4c1d95;
  padding: 1px 5px;
  border-radius: 4px;
  font-size: 12px;
  font-family: var(--font-mono, monospace);
}
.test-ai-body :deep(b) { color: #5b21b6; }

/* Answer state styling on top of existing test-answer styles */
.test-answer.is-correct {
  border-color: #10b981 !important;
  background: #ecfdf5 !important;
}
.test-answer.is-wrong {
  border-color: #ef4444 !important;
  background: #fef2f2 !important;
}

/* Multi-choice tick in the letter slot */
.test-answer-letter svg { color: var(--accent); }
.test-answer.is-correct .test-answer-letter svg { color: #10b981; }
.test-answer.is-wrong .test-answer-letter svg { color: #ef4444; }

/* Navigator cells: correct/wrong/answered */
.test-nav-cell.correct { background: #10b981; color: #fff; border-color: #10b981; }
.test-nav-cell.wrong   { background: #ef4444; color: #fff; border-color: #ef4444; }
/* current state still wins via global rule (accent background) when active */
.test-nav-cell.flagged::after {
  content: '';
  position: absolute;
  top: 2px; right: 2px;
  width: 5px; height: 5px;
  background: var(--warning);
  border-radius: 50%;
}
.test-nav-cell { position: relative; cursor: pointer; }

.test-nav-legend .swatch.correct  { background: #10b981; }
.test-nav-legend .swatch.wrong    { background: #ef4444; }
.test-nav-legend .swatch.answered { background: var(--ink); }

.btn-ghost.flagged {
  color: var(--warning);
  border-color: var(--warning);
  background: rgba(245, 158, 11, 0.08);
}

.fade-in-enter-active, .fade-in-leave-active {
  transition: opacity 0.18s ease, transform 0.18s ease;
}
.fade-in-enter, .fade-in-leave-to {
  opacity: 0;
  transform: translateY(-4px);
}
</style>
